// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGaugeConstants.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import constants from 'logic/data/Izenda.ReportPartFieldConstants';
import localizer from 'share/localization/Izenda.Localizer';
import { deburr, find, isNil } from 'lodash/fp';

const FUNCTION = constants.FUNCTION;
const FORMAT_KEY = constants.FORMAT_KEY;

const MAP_TYPES = {
  World: 'World',
  Continent: 'Continent',
  Country: 'Country',
  Google: 'Google'
};

const CHART_TYPES = {
  get World() {
    return localizer.getText('REPORT_PART_MAP_CONSTANTS_WORLD');
  },
  get Continent() {
    return localizer.getText('REPORT_PART_MAP_CONSTANTS_CONTINENT');
  },
  get Country() {
    return localizer.getText('REPORT_PART_MAP_CONSTANTS_COUNTRY');
  },
  get Google() {
    return localizer.getText('REPORT_PART_MAP_CONSTANTS_GOOGLE');
  }
};

const ContainerName = {
  // Point Options

  POINT_OPTIONS: 'pointOptions', // save to back-end
  COUNTRY: 'country',
  STATE: 'state',
  COUNTY: 'county',
  CITY: 'city',
  POSTAL_CODE: 'postalCode',
  ADDRESS: 'address',
  LATITUDE: 'latitude',
  LONGTITUDE: 'longtitude',

  // Values
  SHADING_VALUES: 'shadingMetric',
  BUBBLE_VALUES: 'bubbleMetrics',

  THRESHOLDS: 'metricThresholds',
  THRESHOLDS_SUBTOTAL: 'metricThresholdSubtotals'
};

const POINT_OPTIONS_CONTAINERS = [
  ContainerName.COUNTRY,
  ContainerName.STATE,
  ContainerName.COUNTY,
  ContainerName.CITY,
  ContainerName.POSTAL_CODE,
  ContainerName.ADDRESS,
  ContainerName.LATITUDE,
  ContainerName.LONGTITUDE
];
const VALUES_CONTAINERS = [ContainerName.SHADING_VALUES, ContainerName.BUBBLE_VALUES];

const hcKeyMapping = {
  key: 'hc-key',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const hca2Mapping = {
  key: 'hc-a2',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const nameMapping = {
  key: 'name',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const countryAbbrevMapping = {
  key: 'country-abbrev',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const isoA3Mapping = {
  key: 'iso-a3',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const isoA2Mapping = {
  key: 'iso-a2',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const codeMapping = {
  key: 'code',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const code2Mapping = {
  key: 'code2',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const countryCodeMapping = {
  key: 'countryCode',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const countryNameMapping = {
  key: 'countryName',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const countryCode2Mapping = {
  key: 'countryCode2',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const code3Mapping = {
  key: 'code3',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const countryCode3Mapping = {
  key: 'countryCode3',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const stateFipsMapping = {
  key: 'state-fips',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const flipsMapping = {
  key: 'flips',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const fipsMapping = {
  key: 'fips',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const woeNameMapping = {
  key: 'woeName',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const hcA2Mapping = {
  key: 'hcA2',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const admin2CodeMapping = {
  key: 'admin2Code',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const geonameIdMapping = {
  key: 'geonameId',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const asciiNameMapping = {
  key: 'asciiName',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const postalCodeMapping = {
  key: 'postalCode',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const postalNameMapping = {
  key: 'placeName',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const alternateNamesMapping = {
  key: 'alternateNames',
  compare: (operand1, operand2) =>
    !isNil(operand1) && !!find(item => String.compareString(deburr(operand2), deburr(item)), operand1.split(','))
};
const stateNameMapping = {
  key: 'stateName',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const stateCodeMapping = {
  key: 'stateCode',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const provinceMapping = {
  key: 'province',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const countyNameMapping = {
  key: 'countyName',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const cityNameMapping = {
  key: 'cityName',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};
const addressValueMapping = {
  key: 'addressValue',
  compare: (operand1, operand2) => String.compareString(deburr(operand1), deburr(operand2))
};

const MappingPointKeys = {};

MappingPointKeys[ContainerName.COUNTRY] = {};

MappingPointKeys[ContainerName.STATE] = {
  [ContainerName.COUNTRY]: [countryCodeMapping, countryCode2Mapping, countryCode3Mapping, countryNameMapping]
};

MappingPointKeys[ContainerName.COUNTY] = {
  [ContainerName.COUNTRY]: [code2Mapping, code3Mapping, countryCode2Mapping, countryCode3Mapping],
  [ContainerName.STATE]: [
    hcKeyMapping,
    hca2Mapping,
    stateFipsMapping,
    flipsMapping,
    woeNameMapping,
    hcA2Mapping,
    stateNameMapping,
    stateCodeMapping
  ]
};

MappingPointKeys[ContainerName.POSTAL_CODE] = {
  [ContainerName.COUNTRY]: [countryCodeMapping, countryNameMapping],
  [ContainerName.STATE]: [provinceMapping]
};

MappingPointKeys[ContainerName.CITY] = {
  [ContainerName.COUNTRY]: [countryCodeMapping, countryCode3Mapping, countryNameMapping],
  [ContainerName.STATE]: [hcA2Mapping, woeNameMapping],
  [ContainerName.COUNTY]: [admin2CodeMapping]
};

MappingPointKeys[ContainerName.ADDRESS] = {
  [ContainerName.COUNTRY]: [countryNameMapping],
  [ContainerName.STATE]: [stateNameMapping],
  [ContainerName.COUNTY]: [countyNameMapping],
  [ContainerName.CITY]: [cityNameMapping],
  [ContainerName.POSTAL_CODE]: [postalCodeMapping]
};

/* MAPPING_KEY */
const MappingKey = {};
MappingKey[ContainerName.COUNTRY] = [
  hcKeyMapping,
  hca2Mapping,
  nameMapping,
  countryAbbrevMapping,
  isoA3Mapping,
  isoA2Mapping,
  code2Mapping,
  code3Mapping,
  alternateNamesMapping
];
MappingKey[ContainerName.STATE] = [
  hcKeyMapping,
  hca2Mapping,
  stateFipsMapping,
  flipsMapping,
  nameMapping,
  woeNameMapping,
  hcA2Mapping
];
MappingKey[ContainerName.COUNTY] = [
  hcKeyMapping,
  hca2Mapping,
  stateFipsMapping,
  fipsMapping,
  nameMapping,
  admin2CodeMapping
];
MappingKey[ContainerName.CITY] = [geonameIdMapping, nameMapping, asciiNameMapping, alternateNamesMapping];
MappingKey[ContainerName.POSTAL_CODE] = [postalCodeMapping, postalNameMapping];
MappingKey[ContainerName.ADDRESS] = [addressValueMapping];
MappingKey['GOOGLE'] = [codeMapping, nameMapping];

/* Default function for each fields based on its container */
const DefaultFunction = {};

DefaultFunction[ContainerName.COUNTRY] = DefaultFunction[ContainerName.STATE] = DefaultFunction[
  ContainerName.COUNTY
] = DefaultFunction[ContainerName.CITY] = DefaultFunction[ContainerName.POSTAL_CODE] = DefaultFunction[
  ContainerName.ADDRESS
] = DefaultFunction[ContainerName.LATITUDE] = DefaultFunction[ContainerName.LONGTITUDE] = DefaultFunction[
  ContainerName.COUNTRY
] = {
  Datetime: {
    FUNCTION: FUNCTION.GROUP,
    FORMAT: FORMAT_KEY.YEAR
  },
  Time: {
    FUNCTION: FUNCTION.GROUP,
    FORMAT: FORMAT_KEY.DEFAULT_TIME
  },

  Others: {
    FUNCTION: FUNCTION.GROUP,
    FORMAT: FORMAT_KEY.NONE
  }
};

DefaultFunction[ContainerName.SHADING_VALUES] = DefaultFunction[ContainerName.BUBBLE_VALUES] = {
  Numeric: {
    FUNCTION: FUNCTION.SUM,
    FORMAT: FORMAT_KEY.NONE
  },

  Money: {
    FUNCTION: FUNCTION.SUM,
    FORMAT: FORMAT_KEY.NONE
  },

  Others: {
    FUNCTION: FUNCTION.COUNT,
    FORMAT: FORMAT_KEY.NONE
  }
};

/* Maximum number of fields for each container */
const MaximumField = {};

MaximumField[ContainerName.COUNTRY] = MaximumField[ContainerName.STATE] = MaximumField[
  ContainerName.COUNTY
] = MaximumField[ContainerName.CITY] = MaximumField[ContainerName.POSTAL_CODE] = MaximumField[
  ContainerName.LATITUDE
] = MaximumField[ContainerName.LONGTITUDE] = MaximumField[ContainerName.COUNTRY] = MaximumField[
  ContainerName.SHADING_VALUES
] = 1;

/* Messages */
const Message = {};

/* Other rules */
const POINTS_OPTIONS_ORDER = [
  ContainerName.COUNTRY,
  ContainerName.STATE,
  ContainerName.COUNTY,
  ContainerName.CITY,
  ContainerName.POSTAL_CODE,
  ContainerName.ADDRESS,
  ContainerName.LATITUDE,
  ContainerName.LONGTITUDE
];

const POINTS_OPTIONS_FOR_SHADING = [ContainerName.COUNTRY, ContainerName.STATE, ContainerName.COUNTY];
const POINTS_OPTIONS_FOR_BUBBLE_ALL = [
  ContainerName.COUNTRY,
  ContainerName.STATE,
  ContainerName.COUNTY,
  ContainerName.CITY,
  ContainerName.POSTAL_CODE,
  ContainerName.ADDRESS,
  ContainerName.LATITUDE,
  ContainerName.LONGTITUDE
];
const POINTS_OPTIONS_FOR_BUBBLE_WITHOUT_LONGLAT = [
  ContainerName.COUNTRY,
  ContainerName.STATE,
  ContainerName.COUNTY,
  ContainerName.CITY,
  ContainerName.POSTAL_CODE,
  ContainerName.ADDRESS
];

// anytime when those values change >> drilldownInfo need to reset
const PROPS_APPLY_NEW_DRILLDOWN = ['chartType', 'continent', 'country', 'state'];

const DRILLDOWN_LEVELS = {
  WORLD: 'WORLD',
  CONTINENT: 'CONTINENT',
  COUNTRY: 'COUNTRY',
  STATE: 'STATE'
};

const DRILLDOWN_ORDERS = [
  DRILLDOWN_LEVELS.WORLD,
  DRILLDOWN_LEVELS.CONTINENT,
  DRILLDOWN_LEVELS.COUNTRY,
  DRILLDOWN_LEVELS.STATE
];

/* Map Constant UI */
const MapUIConstant = {
  backgroundMapColor: '#f2f2f2',
  hoverColor: '#BADA55',
  bubbleMinSize: 4,
  bubbleMaxSize: '10%'
};

/* Key to get value for map label */
const DATA_LABELS = {
  WORLD: 'hc-a2',
  CONTINENT: 'hc-a2',
  COUNTRY: 'postal-code',
  STATE: 'name'
};

const FUNCTION_CONFIG = {
  POINT_OPTIONS: {
    SUPPORTED: {
      Datetime: [FUNCTION.GROUP, FUNCTION.GROUP_DAYS_OLD],
      DEFAULT: [FUNCTION.GROUP]
    }
  },

  VALUES: {
    UNSUPPORTED: {
      DEFAULT: [FUNCTION.NONE, FUNCTION.GROUP],
      Datetime: [FUNCTION.NONE, FUNCTION.GROUP, FUNCTION.MAXIMUM, FUNCTION.MINIMUM]
    }
  }
};

const DEFAULT_SETTING_VALUE = {
  borderDefault: {
    'izEntire.borderColor': '#000',
    'izEntire.borderWidth': undefined,
    'izEntire.borderStyle': 'solid'
  },
  borderNew: {
    'izEntire.borderColor': undefined,
    'izEntire.borderWidth': undefined,
    'izEntire.borderStyle': undefined
  },
  backGroundColorDefault: {
    'izEntire.backgroundColor': '#fff',
    'izPlot.backgroundColor': undefined
  },
  backGroundColorNew: {
    'izEntire.backgroundColor': undefined,
    'izPlot.backgroundColor': undefined
  },
  refreshIntervalDefault: {
    enable: false,
    isAll: true,
    latestRecord: 0,
    updateInterval: 0
  }
};

const GOOGLE_MAPS_DEFAULT_OPTION = {
  zoom: 1,
  center: {
    lat: 0,
    lng: 0
  }
};

const GOOGLE_MAPS_CONSTANTS = {
  MAX_BUBBLE_SCALE: 20,
  MIN_BUBBLE_SIZE: 5,
  DEFAULT_BUBBLE_STROKE_WEIGHT: 1,
  HIGHLIGHT_BUBBLE_STROKE_WEIGHT: 2,
  BUBBLE_FILL_OPACITY: 0.5,
  SHADING_STROKE_OPACITY: 0.8,
  DEFAULT_SHADING_STROKE_WEIGHT: 1.8,
  HIGHLIGHT_SHADING_STROKE_WEIGHT: 2,
  DEFAULT_SHADING_FILL_OPACITY: 0.8,
  HIGHLIGHT_SHADING_FILL_OPACITY: 0.7,
  GRADIENT_BAR_MARKER_PORTION: 4
};

const PROVISION_DATA_NOTIFICATION_MESSAGE_KEY = {
  PROVISIONING: 'ProvisionImporting',
  SUCCESS: 'ProvisionSuccess',
  ERROR: 'ProvisionError'
};

export default {
  CHART_TYPES,
  MAP_TYPES,
  ContainerName,
  DefaultChartType: MAP_TYPES.World,
  DefaultGEOBackground: 'world',
  DefaultContinent: 'central-america',
  DefaultCountry: 'al',

  DefaultFunction,
  MaximumField,

  MappingKey,
  MapUIConstant,

  PointOptionsContainers: POINT_OPTIONS_CONTAINERS,
  ValuesContainers: VALUES_CONTAINERS,
  ThresholdContainer: ContainerName.THRESHOLDS,
  ThresholdSubtotalsContainer: ContainerName.THRESHOLDS_SUBTOTAL,

  PointsOptionsOrder: POINTS_OPTIONS_ORDER,
  MappingPointKeys: MappingPointKeys,
  PointsOptionsForShading: POINTS_OPTIONS_FOR_SHADING,
  PointsOptionsForBubbleAll: POINTS_OPTIONS_FOR_BUBBLE_ALL,
  PointsOptionsForBubbleWithoutLongLat: POINTS_OPTIONS_FOR_BUBBLE_WITHOUT_LONGLAT,
  PropsApplyNewDrilldown: PROPS_APPLY_NEW_DRILLDOWN,
  DrilldownLevels: DRILLDOWN_LEVELS,
  DrilldownOrders: DRILLDOWN_ORDERS,
  DataLabels: DATA_LABELS,

  get KeyNames() {
    return {
      [ContainerName.COUNTRY]: localizer.getText('REPORT_PART_MAP_POINT_country'),
      [ContainerName.STATE]: localizer.getText('REPORT_PART_MAP_POINT_state'),
      [ContainerName.COUNTY]: localizer.getText('REPORT_PART_MAP_POINT_county'),
      [ContainerName.CITY]: localizer.getText('REPORT_PART_MAP_POINT_city'),
      [ContainerName.POSTAL_CODE]: localizer.getText('REPORT_PART_MAP_POINT_postalCode'),
      [ContainerName.ADDRESS]: localizer.getText('REPORT_PART_MAP_POINT_address'),
      [ContainerName.LATITUDE]: localizer.getText('REPORT_PART_MAP_POINT_latitude'),
      [ContainerName.LONGTITUDE]: localizer.getText('REPORT_PART_MAP_POINT_longtitude'),
      [ContainerName.SHADING_VALUES]: localizer.getText('REPORT_PART_MAP_CONSTANTS_SHADING_METRIC'),
      [ContainerName.BUBBLE_VALUES]: localizer.getText('REPORT_PART_MAP_CONSTANTS_BUBBLE_METRIC')
    };
  },
  FunctionConfig: FUNCTION_CONFIG,
  DEFAULT_SETTING_VALUE,
  PROVISION_DATA_NOTIFICATION_MESSAGE_KEY,
  GOOGLE_MAPS_DEFAULT_OPTION,
  GOOGLE_MAPS_CONSTANTS
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ReportPartMapConstants.js